import React, { useState, useEffect, useRef, useMemo } from 'react';
import SessionsPane from './SessionsPane';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import SettingsPane from './SettingsPane';
import OpenAiChatSessionService from '../../../Services/OpenAiChatSessionService';
import ErrorMessage from '../../../Elements/ErrorMessage';
import FaIcon from '../../../Elements/FaIcon';
import UserGuideTitle from '../../../Elements/UserGuideTitle';

const AI_MODEL_OPTIONS = [
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-5', label: 'GPT-5' },
];

const nowTime = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

const fmtTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

const normalizeMessage = (m) => ({
  MessageId: m?.MessageId ?? m?.messageId ?? m?.Id ?? m?.id ?? `tmp-${Date.now()}-${Math.random()}`,
  Content: m?.Content ?? m?.content ?? m?.Text ?? m?.text ?? '',
  Role: (m?.Role ?? m?.role ?? m?.Sender ?? m?.sender ?? 'system').toLowerCase(),
  Ts: fmtTime(m?.CreatedOnUtc ?? m?.createdOnUtc) || m?.Ts || '',
  FullPrice: m?.FullPrice ?? m?.fullPrice,
  PromptPrice: m?.PromptPrice ?? m?.promptPrice,
  OutputPrice: m?.OutputPrice ?? m?.outputPrice,
  Model: m?.Model ?? m?.aiModel,
});
const normalizeList = (arr) => (Array.isArray(arr) ? arr.map(normalizeMessage) : []);

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 600;
    g.gain.value = 0.08;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.12);
    o.onended = () => ctx.close();
  } catch (e) {
    /* no-op */
  }
}

export default function AiChatAdmin() {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [typing, setTyping] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aiModel, setAiModel] = useState('gpt-3.5-turbo');
  const [temperature, setTemperature] = useState(0);
  const [memory, setMemory] = useState(false);

  const loadingMessagesFor = useRef(null);
  const inputRef = useRef(null);
  const gearRef = useRef(null);

  const active = useMemo(
    () => sessions.find((s) => s.SessionId === selectedSessionId) || null,
    [sessions, selectedSessionId]
  );
  const realCount = messages.filter((m) => m.Role === 'user' || m.Role === 'assistant').length;
  const activeTitle = active ? active.SessionName || 'Untitled conversation' : 'No conversation selected';
  const modelLabel = (AI_MODEL_OPTIONS.find((o) => o.value === aiModel) || {}).label || aiModel;

  const refreshSessions = async () => {
    try {
      const data = await OpenAiChatSessionService.GetSessionList();
      if (data?.error) {
        ErrorMessage.SetErrorMessage(data.error);
        return;
      }
      const converted = OpenAiChatSessionService.ConvertDBItems ? OpenAiChatSessionService.ConvertDBItems(data) : data;
      setSessions(converted || []);
      if ((converted?.length ?? 0) > 0 && !selectedSessionId) {
        setSelectedSessionId(converted[0].SessionId ?? null);
      }
      return converted;
    } catch (err) {
      ErrorMessage.SetErrorMessage('Failed to load sessions. Please try again later.', err);
    }
  };

  useEffect(() => {
    refreshSessions();
  }, []);

  useEffect(() => {
    if (!selectedSessionId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    loadingMessagesFor.current = selectedSessionId;
    const load = async () => {
      setLoading(true);
      setMessages([]);
      try {
        const data = await OpenAiChatSessionService.GetSessionMessagesList(selectedSessionId);
        if (cancelled || loadingMessagesFor.current !== selectedSessionId) return;
        if (data?.error) {
          ErrorMessage.SetErrorMessage(data.error);
          setMessages([{ MessageId: 'err', Content: 'Failed to load messages.', Role: 'system' }]);
          return;
        }
        const raw = OpenAiChatSessionService.ConvertMessageItems
          ? OpenAiChatSessionService.ConvertMessageItems(data)
          : data;
        setMessages(normalizeList(raw));
      } catch (err) {
        if (cancelled) return;
        ErrorMessage.SetErrorMessage('Failed to load conversation messages. Please try again later.', err);
        setMessages([{ MessageId: 'err', Content: 'Failed to load messages.', Role: 'system' }]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedSessionId]);

  const handleSelect = (id) => {
    setSettingsOpen(false);
    setSelectedSessionId(id || null);
  };

  const handleNewChat = async () => {
    setSettingsOpen(false);
    try {
      const created = await OpenAiChatSessionService.CreateNewSession();
      const refreshed = await refreshSessions();
      if (refreshed && Array.isArray(refreshed)) {
        const match = refreshed.find((s) => s.SessionId === created?.sessionId);
        if (match) setSelectedSessionId(match.SessionId);
      }
      setQ('');
      setTimeout(() => inputRef.current && inputRef.current.focus(), 40);
    } catch (err) {
      ErrorMessage.SetErrorMessage('Failed to create a new conversation.', err);
    }
  };

  const copy = (text) => {
    const plain = String(text || '').replace(/\*\*|`/g, '');
    try {
      if (navigator?.clipboard) navigator.clipboard.writeText(plain);
    } catch (e) {
      /* no-op */
    }
    playBeep();
    ErrorMessage.SetSuccessMessage('Copied to clipboard');
  };

  const send = async () => {
    setSettingsOpen(false);
    const text = (input ?? '').trim();
    if (!text || typing) return;
    if (!selectedSessionId) {
      ErrorMessage.SetNotification('Select a conversation first');
      return;
    }
    const userMsg = { MessageId: `temp-${Date.now()}`, Content: text, Role: 'user', Ts: nowTime() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setTyping(true);
    try {
      const response = await OpenAiChatSessionService.SendMessage({
        SessionId: selectedSessionId,
        Content: text,
        Role: 'user',
        AiModel: aiModel,
        Temperature: temperature,
        Memory: memory,
      });
      if (response?.error) {
        ErrorMessage.SetErrorMessage('Failed to send message. Please try again.', response.error);
        return;
      }
      const assistant = normalizeMessage({
        MessageId: response?.MessageId ?? response?.messageId,
        Content: response?.Content ?? response?.content,
        Role: response?.Role ?? response?.role ?? 'assistant',
        FullPrice: response?.fullPrice,
        PromptPrice: response?.promptPrice,
        OutputPrice: response?.outputPrice,
        Model: response?.aiModel,
      });
      if (!assistant.Ts) assistant.Ts = nowTime();
      if (response?.sessionName) {
        setSessions((prev) =>
          prev.map((s) => (s.SessionId === selectedSessionId ? { ...s, SessionName: response.sessionName } : s))
        );
      }
      setMessages((prev) => [...prev, assistant]);
      await refreshSessions();
    } catch (err) {
      ErrorMessage.SetErrorMessage('Failed to send message. Please try again later.', err);
    } finally {
      setTyping(false);
    }
  };

  return (
    <div className='contentwrapper bravo-ds ac-page'>
      <div className='page-head'>
        <div className='page-title'>
          <span className='eq-mark'>
            <span></span>
            <span></span>
          </span>
          <div className='page-title-text'>
            <div className='eyebrow'>AI Tools</div>
            <h1>
              <UserGuideTitle title='AI Chat' />
            </h1>
          </div>
        </div>
        <div className='ac-actions'>
          <button
            className='btn'
            onClick={handleNewChat}
          >
            <FaIcon iconName='fa-solid fa-plus' /> New chat
          </button>
          <div className='divider' />
          <button
            className='icon-btn'
            ref={gearRef}
            onClick={() => setSettingsOpen((o) => !o)}
            title='Chat settings'
            aria-label='Chat settings'
          >
            <FaIcon iconName='fa-solid fa-cog' />
          </button>
        </div>
      </div>

      <div className='ac-shell'>
        <SessionsPane
          sessions={sessions}
          selectedSessionId={selectedSessionId}
          onSelect={handleSelect}
          onNew={handleNewChat}
          q={q}
          setQ={setQ}
        />

        <section className='ac-main'>
          <div className='ac-bar'>
            <div className='ct'>
              <b>{activeTitle}</b>
              <span>
                {realCount} message{realCount === 1 ? '' : 's'}
                {active ? ` · #${active.SessionId}` : ''}
              </span>
            </div>
            <div className='sp' />
            <button
              className='ac-model-chip'
              onClick={() => setSettingsOpen((o) => !o)}
            >
              <span className='mdot' /> {modelLabel} <FaIcon iconName='fa-solid fa-chevron-down' />
            </button>
          </div>

          <MessageList
            messages={messages}
            typing={typing}
            loading={loading}
            onCopy={copy}
            inputRef={inputRef}
            onSeedPrompt={setInput}
          />

          <ChatInput
            input={input}
            setInput={setInput}
            onSend={send}
            disabled={typing || !selectedSessionId}
            inputRef={inputRef}
          />
        </section>
      </div>

      {settingsOpen ? (
        <SettingsPane
          anchorRef={gearRef}
          aiModel={aiModel}
          setAiModel={setAiModel}
          temperature={temperature}
          setTemperature={setTemperature}
          memory={memory}
          setMemory={setMemory}
          aiModelOptions={AI_MODEL_OPTIONS}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}
    </div>
  );
}
